import { useReports } from './hooks/useReports';
import { exportToExcel } from './lib/excelExport';
import { useState } from 'react';

export default function GSTReports() {
  const { gst1, gst3b } = useReports();
  const [period, setPeriod] = useState({ month: '', year: '' });
  const [activeReport, setActiveReport] = useState('gstr1');
  
  const gst1Query = gst1(period);
  const gst3bQuery = gst3b(period);
  
  if (gst1Query.isLoading || gst3bQuery.isLoading) {
    return <div className="p-6">Loading...</div>;
  }
  
  const gst1Data = gst1Query.data?.data || [];
  const gst3bData = gst3bQuery.data?.data || [];
  const summary = gst1Query.data?.summary || {};
  
  const handleExportGST1 = () => {
    exportToExcel('GSTR1_Report', [{
      name: 'GSTR1',
      headers: ['Invoice No', 'Date', 'Customer', 'GSTIN', 'Taxable Value', 'CGST', 'SGST', 'IGST', 'Total'],
      data: gst1Data.map((d: any) => [
        d.invoiceNo, d.date, d.customer, d.gstin, d.taxableValue, d.cgst, d.sgst, d.igst, d.total
      ])
    }]);
  };
  
  const handleExportGST3B = () => {
    exportToExcel('GSTR3B_Report', [{
      name: 'GSTR3B',
      headers: ['Description', 'Taxable Value', 'CGST', 'SGST', 'IGST', 'Total Tax'],
      data: gst3bData.map((d: any) => [
        d.description, d.taxableValue, d.cgst, d.sgst, d.igst, d.totalTax
      ])
    }]);
  };
  
  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-800">GST Reports</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setActiveReport('gstr1')}
            className={`px-4 py-2 rounded-lg ${activeReport === 'gstr1' ? 'bg-[var(--color-primary)] text-white' : 'bg-gray-100 text-gray-600'}`}
          >
            GSTR-1
          </button>
          <button
            onClick={() => setActiveReport('gstr3b')}
            className={`px-4 py-2 rounded-lg ${activeReport === 'gstr3b' ? 'bg-[var(--color-primary)] text-white' : 'bg-gray-100 text-gray-600'}`}
          >
            GSTR-3B
          </button>
        </div>
      </div>
      
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex gap-4 items-center">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
            <select
              className="input"
              value={period.month}
              onChange={e => setPeriod({ ...period, month: e.target.value })}
            >
              <option value="">Select Month</option>
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                  {new Date(0, i).toLocaleString('default', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
            <select
              className="input"
              value={period.year}
              onChange={e => setPeriod({ ...period, year: e.target.value })}
            >
              <option value="">Select Year</option>
              {Array.from({ length: 5 }, (_, i) => {
                const year = new Date().getFullYear() - i;
                return <option key={year} value={year}>{year}</option>;
              })}
            </select>
          </div>
          <div className="flex gap-2 mt-6">
            <button
              className="btn-primary"
              onClick={activeReport === 'gstr1' ? handleExportGST1 : handleExportGST3B}
            >
              <i className="ri-file-excel-2-line mr-2"></i>
              Export Excel
            </button>
            <button className="btn-secondary">
              <i className="ri-file-pdf-line mr-2"></i>
              Export PDF
            </button>
          </div>
        </div>
      </div>
      
      {activeReport === 'gstr1' && (
        <>
          <div className="grid md:grid-cols-4 gap-4">
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="text-sm text-gray-600">Total Taxable Value</div>
              <div className="text-2xl font-bold text-[var(--color-primary)]">₹{(summary.taxableValue || 0).toLocaleString()}</div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="text-sm text-gray-600">Total CGST</div>
              <div className="text-2xl font-bold text-green-600">₹{(summary.cgst || 0).toLocaleString()}</div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="text-sm text-gray-600">Total SGST</div>
              <div className="text-2xl font-bold text-blue-600">₹{(summary.sgst || 0).toLocaleString()}</div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="text-sm text-gray-600">Total IGST</div>
              <div className="text-2xl font-bold text-purple-600">₹{(summary.igst || 0).toLocaleString()}</div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-[var(--color-accent)]/40">
                  <tr>
                    <th className="th">Invoice No</th>
                    <th className="th">Date</th>
                    <th className="th">Customer</th>
                    <th className="th">GSTIN</th>
                    <th className="th">Taxable Value</th>
                    <th className="th">CGST</th>
                    <th className="th">SGST</th>
                    <th className="th">IGST</th>
                    <th className="th">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {gst1Data.map((row: any, index: number) => (
                    <tr key={index} className="tr">
                      <td className="td">{row.invoiceNo}</td>
                      <td className="td">{row.date}</td>
                      <td className="td">{row.customer}</td>
                      <td className="td">{row.gstin}</td>
                      <td className="td">₹{row.taxableValue?.toLocaleString()}</td>
                      <td className="td">₹{row.cgst?.toLocaleString()}</td>
                      <td className="td">₹{row.sgst?.toLocaleString()}</td>
                      <td className="td">₹{row.igst?.toLocaleString()}</td>
                      <td className="td font-semibold">₹{row.total?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      
      {activeReport === 'gstr3b' && (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-[var(--color-accent)]/40">
                <tr>
                  <th className="th">Description</th>
                  <th className="th">Taxable Value</th>
                  <th className="th">CGST</th>
                  <th className="th">SGST</th>
                  <th className="th">IGST</th>
                  <th className="th">Total Tax</th>
                </tr>
              </thead>
              <tbody>
                {gst3bData.map((row: any, index: number) => (
                  <tr key={index} className="tr">
                    <td className="td">{row.description}</td>
                    <td className="td">₹{row.taxableValue?.toLocaleString()}</td>
                    <td className="td">₹{row.cgst?.toLocaleString()}</td>
                    <td className="td">₹{row.sgst?.toLocaleString()}</td>
                    <td className="td">₹{row.igst?.toLocaleString()}</td>
                    <td className="td font-semibold">₹{row.totalTax?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}